import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { notifyApp } from "utils/yeloAppBridge.util";
import { goBack } from "utils/navigation.util";

type PartnerBackButtonProps = {
  className?: string;
  /**
   * In-app destination to return to. Screens that can be reached from more
   * than one place pass the screen the reader actually came from; without
   * it the button leaves the webview entirely, which is right for a dead
   * end like Not authorised but wrong anywhere with a screen behind it.
   */
  to?: string;
  /**
   * `to` is the screen right behind this one in history (e.g. a booking
   * opened from the list): back pops to it instead of opening it again, so
   * the phone's back button afterwards does not walk forward into this
   * screen. Without it, `to` replaces this screen.
   */
  pop?: boolean;
  /** Visible text beside the chevron (e.g. "Back to Yelo" on home). */
  label?: string;
};

export default function PartnerBackButton({
  className,
  to,
  pop = false,
  label,
}: PartnerBackButtonProps) {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const handleBack = () => {
    if (to) {
      if (pop) goBack(navigate, to);
      else navigate(to, { replace: true });
      return;
    }
    /* Nothing behind this screen in the webview — hand control back to the
       Yelo app, which owns the screen the user came from. */
    import("services/mixpanel.service")
      .then(({ trackWebviewExited }) => trackWebviewExited("back_to_yelo"))
      .catch(() => {});
    notifyApp("close");
  };

  return (
    <button
      type="button"
      className={`${className ?? ""}${label ? " has-label" : ""}`}
      aria-label={label ? undefined : String(i18n.t("back"))}
      onClick={handleBack}
    >
      {label ? (
        <>
          <span className="partner-back__icon" aria-hidden="true">
            <svg
              className={`ico${i18n.language === "ar" ? " rotate" : ""}`}
              viewBox="0 0 24 24"
              width={18}
              height={18}
            >
              <path d="M14.5 5.5 8 12l6.5 6.5" />
            </svg>
          </span>
          <span className="partner-back__label">{label}</span>
        </>
      ) : (
        <svg
          className={`ico${i18n.language === "ar" ? " rotate" : ""}`}
          viewBox="0 0 24 24"
          width={17}
          height={17}
          aria-hidden="true"
        >
          <path d="M15 5l-7 7 7 7" />
        </svg>
      )}
    </button>
  );
}
